import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Dialog, Toolbar, IconButton, Typography, Button, Icon, LinearProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import useStyles from './ReassignModal.styles';
import { get, set, size } from 'global/lib/lodash';
import Alert from '@mui/material/Alert';
import { useScreen, Loader } from 'global/styles';
import sendData from 'redux/actions/sendData';
import Form from 'global/components/form';
import { Column, Row } from '../../../global/components/row';
import { Text } from '../../../global/components/text';
import { TradeSelect } from '../../../global/components/trade-select';
import { CheckpointScore } from '../../report/ChecklistScores';
import { cleanCopy } from '../../../global/utils';
import { AUTOTAG } from '../../report/AssessmentScore';
import { SETTINGS, taskOptions } from '../../admin/account-settings';
import { getAccountSetting } from 'redux/actions/userLogin';
import { useTaskTypes } from '../task-type-utils/useTaskTypes';
import { getAvailableTaskTypes, getCustomFieldInputs, getCloseoutFromTaskType, getCloseoutDefaults, getStandardFieldRequirements } from '../task-type-utils';

// eslint-disable-next-line import/no-anonymous-default-export
export default (props) => {
  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);
  const [formState, setFormState] = useState({});
  const [items, setItems] = useState({});
  const [defaultedTradeIds, setDefaultedTradeIds] = useState({});
  const [open, setOpen] = useState({});
  const [stepMode, setStepMode] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [stepFormStates, setStepFormStates] = useState({});
  const itemsRef = useRef({});
  const defaultedTradeIdsRef = useRef({});
  const formRef = useRef(null);
  const stepFormRef = useRef(null);

  const { isMobileXS } = useScreen();

  const classes = useStyles();

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(() => {
    defaultedTradeIdsRef.current = defaultedTradeIds;
  }, [defaultedTradeIds]);

  const getTradeIds = (trades = []) =>
    (trades || [])
      .map((trade) => get(trade, 'id'))
      .filter((id) => id !== undefined && id !== null && id !== '')
      .map((id) => String(id));

  const mergeTrades = (...tradeLists) => {
    const seen = new Set();
    const merged = [];

    tradeLists.flat().forEach((trade) => {
      const rawTradeId = get(trade, 'id');
      if (rawTradeId === undefined || rawTradeId === null || rawTradeId === '') {
        return;
      }

      const tradeId = String(rawTradeId);
      if (seen.has(tradeId)) {
        return;
      }

      seen.add(tradeId);
      merged.push(trade);
    });

    return merged;
  };

  const getRemainingDefaultedTradeIds = (ids = [], trades = []) => {
    const selectedTradeIds = new Set(getTradeIds(trades));
    return (ids || []).filter((id) => selectedTradeIds.has(String(id)));
  };

  const setCheckpointDefaultedTradeIds = (state, checklistId, checkpointId, ids = []) => {
    const nextChecklist = { ...get(state, checklistId, {}) };
    const nextIds = [...new Set((ids || []).map((id) => String(id)).filter((id) => !!id))];

    if (size(nextIds)) {
      nextChecklist[checkpointId] = nextIds;
    } else {
      delete nextChecklist[checkpointId];
    }

    if (size(nextChecklist)) {
      return {
        ...state,
        [checklistId]: nextChecklist,
      };
    }

    const nextState = { ...state };
    delete nextState[checklistId];
    return nextState;
  };

  const syncTradeState = (nextItems, nextDefaultedTradeIds) => {
    itemsRef.current = nextItems;
    defaultedTradeIdsRef.current = nextDefaultedTradeIds;
    setItems(nextItems);
    setDefaultedTradeIds(nextDefaultedTradeIds);
  };

  // Flatten all checkpoints into a list for step-through mode
  const allCheckpoints = useMemo(() => {
    const cps = [];
    Object.keys(items).forEach((k) => {
      const check = items[k];
      if (check && check.checkpoints) {
        check.checkpoints.forEach((cp) => {
          cps.push({ ...cp, _checklist: check.checklist });
        });
      }
    });
    return cps;
  }, [items]);

  const handleClose = (opts) => {
    setStepMode(false);
    setStepIndex(0);
    setStepFormStates({});
    props.onClose(opts);
  };

  const formSubmit = async (overrideStepFormStates) => {
    try {
      if (loading) {
        return;
      }

      setLoading(true);
      if (props.onFormSubmit) {
        const finalStepStates = overrideStepFormStates || stepFormStates;
        const submitFormState = stepMode
          ? { ...formState, perCheckpointOverrides: finalStepStates }
          : formState;
        await props.onFormSubmit(submitFormState, items);
      } else {
        await Promise.all(
          props.tasks.map(async (task) => {
            const data = {
              data: {
                id: task.id,
                type: 'tasks',
                attributes: {
                  due_date: get(formState, 'dueDate'),
                  meta: get(formState, 'meta', {}),
                },
                relationships: {
                  assignees: {
                    data: get(formState, 'assignedTo', []).map((a) => ({ type: 'users', role: 'assignee', id: a.id })),
                  },
                },
              },
            };
            await sendData({
              url: `/tasks/${task.id}`,
              method: 'patch',
              data,
            });
          })
        );
      }

      setLoading(false);
      handleClose(true);
    } catch (err) {
      setError(`Error: ${err.message}`);
      setLoading(false);
    }
  };

  const defaults = {
    contentTitle: 'Assignee',
    contentSub: 'Who would you like to assign these tasks to?',
    tradeTitle: 'Trades',
    tradeSub: 'Which trades are responsible for these tasks?',
    ...props,
  };

  useEffect(() => {
    if (!!props.open) {
      defaultedTradeIdsRef.current = {};
      setDefaultedTradeIds({});
      const checklist = {
        // [id]: {
        //   loaded: false,
        //   checklist: {},
        //   checkpoints: [],
        // }
      };
      const selectedItems = cleanCopy(props.selectedItems);
      Object.keys({ ...selectedItems }).map((k) => {
        const item = get(selectedItems, k);
        if (item && item.itemType === 'checkpoint') {
          set(checklist, [item.checklistId, 'checklist'], item.checklist);
          if (item.subscores) {
            item.subscores.map((ss) => {
              if (get(ss, 'notes') !== AUTOTAG && (!!get(ss, 'notes') || size(get(ss, 'images', [])) > 0)) {
                const exists = !!get(checklist, [item.checklistId, 'checkpoints'], []).find(f => f.id == ss.id);
                if (exists) { return }
                set(checklist, [item.checklistId, 'checkpoints'], [...get(checklist, [item.checklistId, 'checkpoints'], []), ss]);
              }
            });
            delete item.subscores;
          } else {
            set(checklist, [item.checklistId, 'checkpoints'], [...get(checklist, [item.checklistId, 'checkpoints'], []), item]);
          }
        }
      });

      itemsRef.current = checklist;
      setItems(checklist);
    } else {
      itemsRef.current = {};
      defaultedTradeIdsRef.current = {};
      setItems({});
      setDefaultedTradeIds({});
      setStepMode(false);
      setStepIndex(0);
      setStepFormStates({});
    }
  }, [props.open]);

  const setLoadAndUpdateChildren = (check, sel) => {
    const checklistId = get(check, 'checklist.id');
    const currentChecklistState = get(itemsRef.current, checklistId, {});
    const loaded = get(currentChecklistState, 'loaded', false);
    const checklist = {
      ...get(currentChecklistState, 'checklist', get(check, 'checklist', {})),
      tradeTypes: sel,
    };

    let nextDefaultedTradeIds = defaultedTradeIdsRef.current;
    const checkpoints = get(currentChecklistState, 'checkpoints', []).map((checkpoint) => {
      if (loaded || size(get(checkpoint, 'tradeTypes', []))) {
        return checkpoint;
      }

      nextDefaultedTradeIds = setCheckpointDefaultedTradeIds(nextDefaultedTradeIds, checklistId, checkpoint.id, getTradeIds(sel));

      return {
        ...checkpoint,
        tradeTypes: sel,
      };
    });

    const nextItems = {
      ...itemsRef.current,
      [checklistId]: {
        ...currentChecklistState,
        loaded: true,
        checklist,
        checkpoints,
      },
    };

    syncTradeState(nextItems, nextDefaultedTradeIds);
  };

  const updateChecks = (check, sel, cp) => {
    const checklistId = get(check, 'checklist.id');
    const currentChecklistState = get(itemsRef.current, checklistId, {});
    const loaded = get(currentChecklistState, 'loaded', false);
    const currentChecklist = get(currentChecklistState, 'checklist', get(check, 'checklist', {}));
    const previousChecklistTrades = get(currentChecklist, 'tradeTypes', []);

    let nextDefaultedTradeIds = defaultedTradeIdsRef.current;
    let checkpoints = get(currentChecklistState, 'checkpoints', []);
    let checklist = currentChecklist;

    if (cp) {
      checkpoints = checkpoints.map((checkpoint) => {
        if (checkpoint.id != cp.id) {
          return checkpoint;
        }

        nextDefaultedTradeIds = setCheckpointDefaultedTradeIds(
          nextDefaultedTradeIds,
          checklistId,
          checkpoint.id,
          getRemainingDefaultedTradeIds(get(defaultedTradeIdsRef.current, [checklistId, checkpoint.id], []), sel)
        );

        return {
          ...checkpoint,
          tradeTypes: sel,
        };
      });
    } else {
      checklist = { ...currentChecklist, tradeTypes: sel };

      if (loaded) {
        const previousChecklistTradeIds = new Set(getTradeIds(previousChecklistTrades));
        const addedTrades = (sel || []).filter((trade) => !previousChecklistTradeIds.has(String(get(trade, 'id'))));

        checkpoints = checkpoints.map((checkpoint) => {
          const checkpointTradeIds = new Set(getTradeIds(get(checkpoint, 'tradeTypes', [])));
          const appendedTrades = addedTrades.filter((trade) => !checkpointTradeIds.has(String(get(trade, 'id'))));

          if (!size(appendedTrades)) {
            return checkpoint;
          }

          return {
            ...checkpoint,
            tradeTypes: mergeTrades(get(checkpoint, 'tradeTypes', []), appendedTrades),
          };
        });
      }
    }

    const nextItems = {
      ...itemsRef.current,
      [checklistId]: {
        ...currentChecklistState,
        loaded: loaded,
        checklist,
        checkpoints,
      },
    };

    syncTradeState(nextItems, nextDefaultedTradeIds);
  };

  const toggleOpen = (check, _i) => {
    const key = `${check.checklist.id}_${_i}`;
    setOpen((o) => ({ ...o, [key]: !o[key] }));
  };

  const renderChecklist = (check, i) => {
    const { checklist, checkpoints } = check;

    if (!checklist) {
      return null;
    }

    const fixMargins = { marginLeft: 5, pointerEvents: 'none' };

    const title = (style = {}) => {
      return (
        <Row style={style}>
          <Text bold type="title_medium">{`${get(checklist, 'name', '')} (${size(checkpoints)})`}</Text>
        </Row>
      );
    };

    const CONT = isMobileXS ? Column : Row;

    const renderCP = (cp, _i) => {
      const isOpen = get(open, `${checklist.id}_${_i}`);

      const renderCheckpointScore = (_cp, style) => {
        return <CheckpointScore pssvCheckpoint={_cp} checklist={checklist} showDrop={false} showContent={!!style.cursor && !!isOpen} showDisplayScore={false} />;
      };

      const cpTitle = (style = { cursor: 'pointer', marginLeft: -20, maxWidth: isMobileXS ? undefined : '50%' }) => {
        return (
          <Row style={style} onClick={() => toggleOpen(check, _i)}>
            {renderCheckpointScore(cp, style)}
            {!!style.cursor && <Icon>{isOpen ? 'arrow_drop_down' : 'arrow_right'}</Icon>}
          </Row>
        );
      };

      return (
        <div style={{ marginBottom: isMobileXS ? 10 : 0 }}>
          <CONT>
            {cpTitle()}
            <div style={{ marginLeft: isMobileXS ? 20 : 'auto' }}>
              <TradeSelect
                type="inlineModal"
                value={cp.tradeTypes}
                compare={checklist.tradeTypes}
                checklist={{ ...checklist, checklistId: checklist.id }}
                onChange={(s) => updateChecks(check, s, cp)}
                modalTitle={cpTitle(fixMargins)}
                highlightOriginals
                mutedTradeIds={get(defaultedTradeIds, [checklist.id, cp.id], [])}
                skipDefaults
              />
            </div>
          </CONT>
        </div>
      );
    };

    if (!size(checkpoints)) {
      return null;
    }

    return (
      <div>
        <CONT className={classes.checklistHeader}>
          {title()}
          <div style={{ marginLeft: isMobileXS ? 0 : 'auto' }}>
            <TradeSelect
              checklist={{ ...checklist, checklistId: checklist.id }}
              onChecklistTradesLoaded={(s) => setLoadAndUpdateChildren(check, s)}
              onChange={(s) => updateChecks(check, s)}
              type="inlineModal"
              modalTitle={title({ ...fixMargins, marginLeft: 30, marginTop: 4 })}
            />
          </div>
        </CONT>

        {(checkpoints || []).map(renderCP)}
      </div>
    );
  };

  const showTrades = get(props, 'showTrades', false);
  const showDueDate = get(props, 'showDueDate', true);
  const showTaskDefaults = get(props, 'showTaskDefaults', true);
  const { data: taskTypesData } = useTaskTypes();
  const availableTypes = props.availableTaskTypes || getAvailableTaskTypes(taskTypesData);
  const selectedTaskType = availableTypes.find((tt) => tt.id === get(formState, 'taskTypeId'));
  const showTaskTypeSelector = get(props, 'showTaskTypeSelector', false);
  const isOneByOne = get(selectedTaskType, 'multi_select_behavior') === 'one_by_one';
  const needsTaskType = showTaskTypeSelector && size(availableTypes) > 1 && !get(formState, 'taskTypeId');

  // Update closeout defaults when task type changes
  useEffect(() => {
    if (selectedTaskType && formRef.current) {
      formRef.current.updateState('meta', getCloseoutDefaults(selectedTaskType));
    }
  }, [get(selectedTaskType, 'id')]);

  const currentCheckpoint = allCheckpoints[stepIndex];
  const totalSteps = allCheckpoints.length;

  const enterStepMode = () => {
    setStepMode(true);
    setStepIndex(0);
    setStepFormStates({});
  };

  const saveCurrentStepAndAdvance = () => {
    // Validate step form before advancing
    if (stepFormRef.current) {
      const result = stepFormRef.current.validateRequired();
      if (!result.passes) return;
    }

    // Store current step's custom field values
    const cpId = get(currentCheckpoint, 'id', stepIndex);
    const updatedStepStates = {
      ...stepFormStates,
      [cpId]: {
        custom_field_values: get(formState, 'custom_field_values', {}),
      },
    };
    setStepFormStates(updatedStepStates);

    if (stepIndex < totalSteps - 1) {
      setStepIndex(stepIndex + 1);
    } else {
      // Last step - submit all, pass updated states directly to avoid async race
      formSubmit(updatedStepStates);
    }
  };

  const renderStepContent = () => {
    if (!currentCheckpoint) return null;
    const checklist = currentCheckpoint._checklist;

    return (
      <div>
        <div style={{ marginBottom: 16 }}>
          <LinearProgress variant="determinate" value={((stepIndex + 1) / totalSteps) * 100} />
          <Text type="title_medium" style={{ marginTop: 8 }}>
            Task {stepIndex + 1} of {totalSteps}
          </Text>
        </div>

        <div style={{ marginBottom: 16, padding: '8px 0', borderBottom: '1px solid #eee' }}>
          <Text type="label_small" style={{ color: '#666' }}>{get(checklist, 'name', '')}</Text>
          <CheckpointScore pssvCheckpoint={currentCheckpoint} checklist={checklist} showDrop={false} showContent={false} showDisplayScore={false} />
        </div>

        {selectedTaskType && (
          <Form
            key={`step-${stepIndex}`}
            ref={stepFormRef}
            loading={loading}
            showAllScreens={true}
            formStyle={classes.formStyle}
            isNoMarginContainer
            onFormState={(vals) => {
              setFormState((prev) => ({ ...prev, ...vals }));
            }}
            screens={[
              {
                title: '',
                titleStyle: classes.titleStyle,
                screenContainerStyle: isMobileXS ? classes.screenContainerMobile : classes.screenContainer,
                screenContainer: { props: { className: classes.screenWidth } },
                inputs: [
                  ...getCustomFieldInputs(selectedTaskType, {}),
                ].filter((f) => !!f),
              },
            ]}
          />
        )}

        <div className={classes.buttonContainer}>
          {stepIndex > 0 && (
            <Button
              onClick={() => setStepIndex(stepIndex - 1)}
              startIcon={<Icon>arrow_back</Icon>}
              className={classes.button}
              variant="outlined"
              style={{ marginRight: 8 }}
            >
              Back
            </Button>
          )}
          <Button
            onClick={saveCurrentStepAndAdvance}
            startIcon={loading ? <Loader size={16} /> : <Icon>{stepIndex < totalSteps - 1 ? 'arrow_forward' : 'check'}</Icon>}
            className={classes.button}
            variant="contained"
            disabled={loading}
          >
            {stepIndex < totalSteps - 1 ? 'Next' : 'Done'}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <Dialog className={classes.dialog} fullScreen disableEscapeKeyDown open={props.open} onClose={() => handleClose()}>
      <Toolbar className={classes.toolbar}>
        <IconButton edge="start" color="inherit" onClick={() => handleClose()} aria-label="Close" size="large">
          <CloseIcon />
        </IconButton>

        <div className={classes.barTitle}>
          <Typography variant="h5" className={classes.title}>
            {stepMode ? `${get(selectedTaskType, 'name', 'Task')} ${stepIndex + 1} of ${totalSteps}` : props.title}
          </Typography>
        </div>
      </Toolbar>

      <div className={classes.container}>
        {error && (
          <Alert
            severity="error"
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => {
                  setError();
                }}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {error}
          </Alert>
        )}

        {stepMode ? renderStepContent() : (
          <>
            <div>
              <Typography variant="h5">{defaults.contentTitle}</Typography>
              <Typography variant="subtitle1">{defaults.contentSub}</Typography>
            </div>

            <div>
              <Form
                loading={loading}
                showAllScreens={true}
                formStyle={classes.formStyle}
                onSubmit={formSubmit}
                isNoMarginContainer
                ref={formRef}
                onFormState={(vals) => {
                  if (size(availableTypes) === 1) {
                    setFormState({ ...vals, taskTypeId: get(availableTypes, '0.id') });
                  } else {
                    setFormState(vals);
                  }
                }}
                screens={[
                  {
                    title: '',
                    titleStyle: classes.titleStyle,
                    screenContainerStyle: isMobileXS ? classes.screenContainerMobile : classes.screenContainer,
                    screenContainer: {
                      props: {
                        className: classes.screenWidth,
                      },
                    },
                    inputs: (() => {
                      const stdReqs = selectedTaskType ? getStandardFieldRequirements(selectedTaskType) : [];
                      const isFieldRequired = (fid) => stdReqs.find((f) => f.id === fid)?.is_required;
                      const isFieldEnabled = (fid) => { const f = stdReqs.find((s) => s.id === fid); return f ? f.is_enabled !== false : true; };

                      // Fields auto-handled in assessment context (title auto-generated, trades handled separately, etc.)
                      const ASSESSMENT_SKIP = ['f_title', 'f_reference', 'f_media', ...(showTrades ? ['f_trade'] : [])];

                      const typeSelector = showTaskTypeSelector && size(availableTypes) > 1 && {
                        id: 'taskTypeId',
                        type: 'Select',
                        data: availableTypes.map((tt) => ({ title: tt.name, value: tt.id })),
                        defaultValue: () => get(formState, 'taskTypeId', ''),
                        props: { label: 'Task Type' },
                      };

                      const defaultFieldMap = {
                        f_description: {
                          id: 'description',
                          type: 'TextField',
                          required: isFieldRequired('f_description'),
                          defaultValue: () => get(formState, 'description', ''),
                          props: { placeholder: 'Describe your task', label: 'Description', maxRows: 5, multiline: true },
                        },
                        f_assignee: {
                          id: 'assignedTo',
                          type: 'UserSelect',
                          required: isFieldRequired('f_assignee'),
                          defaultValue: () => get(props, 'tasks.0.assignees', []),
                          data: () => props.assignees,
                          props: { label: 'Assigned To', ...get(props, 'assignedToProps', {}) },
                        },
                        f_due_date: showDueDate ? {
                          id: 'dueDate',
                          type: 'KeyboardDatePicker',
                          required: isFieldRequired('f_due_date'),
                          props: { label: 'Due Date' },
                        } : null,
                        f_trade: {
                          id: 'tradeTypes',
                          type: 'TradeSelect',
                          required: isFieldRequired('f_trade'),
                          defaultValue: () => get(formState, 'tradeTypes', []),
                          props: { label: 'Trade Types' },
                        },
                      };

                      const metaInput = showTaskDefaults && {
                        id: 'meta',
                        type: 'Checkbox',
                        options: selectedTaskType ? getCloseoutFromTaskType(selectedTaskType) : taskOptions(),
                        defaultValue: () => selectedTaskType ? getCloseoutDefaults(selectedTaskType) : getAccountSetting(SETTINGS.taskDefault),
                        props: { label: 'Requirements' },
                      };

                      const fieldOrder = selectedTaskType && get(selectedTaskType, 'field_order');

                      if (fieldOrder && Array.isArray(fieldOrder) && !isOneByOne) {
                        const customInputMap = {};
                        getCustomFieldInputs(selectedTaskType, formState).forEach((input) => {
                          const cfId = input.id.replace('custom_field_values.', '');
                          customInputMap[cfId] = input;
                        });

                        const ordered = [typeSelector];
                        fieldOrder.forEach((fid) => {
                          if (ASSESSMENT_SKIP.includes(fid)) return;
                          if (defaultFieldMap[fid] && isFieldEnabled(fid)) {
                            ordered.push(defaultFieldMap[fid]);
                          } else if (customInputMap[fid]) {
                            ordered.push(customInputMap[fid]);
                          }
                        });

                        // Append custom fields not in field_order
                        Object.keys(customInputMap).forEach((cfId) => {
                          if (!fieldOrder.includes(cfId)) {
                            ordered.push(customInputMap[cfId]);
                          }
                        });

                        ordered.push(metaInput);
                        return ordered.filter(Boolean);
                      }

                      // Fallback: no field_order or isOneByOne
                      return [
                        typeSelector,
                        defaultFieldMap.f_assignee,
                        defaultFieldMap.f_due_date,
                        ...(selectedTaskType && isFieldEnabled('f_description') ? [defaultFieldMap.f_description] : []),
                        ...(selectedTaskType && !isOneByOne ? getCustomFieldInputs(selectedTaskType, formState) : []),
                        metaInput,
                      ].filter(Boolean);
                    })(),
                  },
                ]}
              />

              {showTrades && (
                <div style={{ marginTop: 8 }}>
                  <div>
                    <Typography variant="h5">{defaults.tradeTitle}</Typography>
                    <Typography variant="subtitle1">{defaults.tradeSub}</Typography>
                  </div>

                  {Object.keys(items).map((k, i) => renderChecklist(items[k], i))}
                </div>
              )}

              <div className={classes.buttonContainer}>
                {typeof props.buttons === 'function' ? (
                  props.buttons({ formState })
                ) : isOneByOne && totalSteps > 0 ? (
                  <Button
                    onClick={() => {
                      if (formRef.current) {
                        const result = formRef.current.validateRequired();
                        if (!result.passes) return;
                      }
                      enterStepMode();
                    }}
                    startIcon={<Icon>arrow_forward</Icon>}
                    className={classes.button}
                    variant="contained"
                    disabled={loading || needsTaskType}
                  >
                    {`Continue (${totalSteps} ${totalSteps === 1 ? 'item' : 'items'})`}
                  </Button>
                ) : (
                  <Button onClick={() => {
                    if (formRef.current) {
                      const result = formRef.current.validateRequired();
                      if (!result.passes) return;
                    }
                    formSubmit();
                  }} startIcon={loading ? <Loader size={16} /> : <Icon>person_add</Icon>} className={classes.button} variant="contained" disabled={loading || needsTaskType}>
                    {props.okButton || 'RE-ASSIGN'}
                  </Button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
};
